import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  CpuChipIcon,
  DocumentChartBarIcon,
  CircleStackIcon,
  SparklesIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { analyzeBusinessQuery } from '../lib/groq'
import { clsx } from 'clsx'

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const features = [
  { icon: ChartBarIcon, title: 'Real-time KPI tracking', desc: 'Monitor business metrics in real-time with customizable dashboards.' },
  { icon: CpuChipIcon, title: 'AI-powered insights', desc: 'Machine learning algorithms analyze patterns and surface actionable intelligence.' },
  { icon: DocumentChartBarIcon, title: 'Automated reporting', desc: 'Schedule and generate comprehensive reports automatically.' },
  { icon: CircleStackIcon, title: 'Data aggregation from multiple sources', desc: 'Connect databases, APIs, spreadsheets, and files in one platform.' },
]

const providers = [
  { name: 'Power BI', url: 'https://powerbi.microsoft.com', desc: 'Microsoft business analytics' },
  { name: 'Tableau', url: 'https://www.tableau.com', desc: 'Visual analytics platform' },
  { name: 'Looker', url: 'https://looker.com', desc: 'Google Cloud BI platform' },
  { name: 'Apache Superset', url: 'https://superset.apache.org', desc: 'Open-source data exploration' },
]

export const EnterpriseIntelligence = () => {
  const [query, setQuery] = useState('')
  const [aiResponse, setAiResponse] = useState('')
  const [isQuerying, setIsQuerying] = useState(false)

  const suggestedQuestions = [
    'What are my top-performing services?',
    'Show revenue trends for this quarter',
    'Which channels drive the most conversions?',
    'Predict next month\'s customer growth',
  ]

  const handleQuery = async (q?: string) => {
    const question = q || query.trim()
    if (!question || isQuerying) return
    setQuery('')
    setIsQuerying(true)
    setAiResponse('')
    try {
      const context = 'Business metrics: Revenue $284,500 (+12.5% MoM), Customers 1,842, Conversion 3.8%, Satisfaction 92%. Services: AI Writer 85%, Email Ops 90%, CRM 72%, Chatbot 78%. Channels: Email 35%, WhatsApp 25%, Social 20%, Telegram 15%, Direct 5%.'
      const response = await analyzeBusinessQuery(question, context)
      setAiResponse(response)
    } catch {
      setAiResponse('Unable to process your query right now. Please try again.')
    } finally {
      setIsQuerying(false)
    }
  }
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-metallic-900">Enterprise Intelligence</h1>
        <p className="text-metallic-500 mt-1">AI-powered business performance intelligence and analytics platform.</p>
      </motion.div>

      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((f) => (
          <Card key={f.title} hover>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <f.icon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-metallic-900">{f.title}</h3>
                <p className="text-sm text-metallic-500 mt-1">{f.desc}</p>
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      {/* AI Query Interface */}
      <motion.div variants={item}>
        <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-200">
          <div className="flex items-center gap-3 mb-4">
            <SparklesIcon className="w-6 h-6 text-primary-600" />
            <h3 className="font-semibold text-metallic-900">AI Business Intelligence Query</h3>
            <Badge variant="success" size="sm">Groq</Badge>
          </div>
          <p className="text-sm text-metallic-500 mb-4">
            Ask natural language questions about your business data and get instant AI-powered insights.
          </p>
          <div className="flex items-center gap-2 mb-4">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
              placeholder="e.g., What are my highest-revenue services this quarter?"
              className="flex-1"
              disabled={isQuerying}
            />
            <Button onClick={() => handleQuery()} disabled={isQuerying || !query.trim()}>
              <PaperAirplaneIcon className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap mb-4">
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => handleQuery(q)}
                disabled={isQuerying}
                className="text-xs px-3 py-1.5 bg-white border border-primary-200 text-primary-600 rounded-full hover:bg-primary-100 transition-colors disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
          {isQuerying && (
            <div className="p-4 bg-white rounded-xl border border-primary-100">
              <div className="flex items-center gap-2 text-primary-600">
                <SparklesIcon className="w-4 h-4 animate-pulse" />
                <span className="text-sm font-medium">Analyzing your data...</span>
              </div>
            </div>
          )}
          {aiResponse && !isQuerying && (
            <div className="p-4 bg-white rounded-xl border border-primary-100">
              <p className="text-sm text-metallic-700 whitespace-pre-line leading-relaxed">{aiResponse}</p>
            </div>
          )}
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4">Service Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {providers.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 rounded-xl border border-metallic-200 hover:border-primary-300 hover:bg-primary-50 transition-all group">
                <div>
                  <p className="font-medium text-metallic-900 group-hover:text-primary-600">{p.name}</p>
                  <p className="text-sm text-metallic-500">{p.desc}</p>
                </div>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-metallic-400 group-hover:text-primary-500" />
              </a>
            ))}
          </div>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-r from-primary-50 to-white border-primary-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className="w-6 h-6 text-primary-600" />
            <h3 className="font-semibold text-metallic-900">What's Included</h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {['Custom dashboards', 'Real-time data sync', 'AI trend analysis', 'Export to PDF/Excel', 'Team collaboration', 'API access'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-metallic-600">
                <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </motion.div>
  )
}
